import { useCallback, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useUser } from '@/entities/user/model/user.store';
import { AVD_NETWORK } from '@/entities/user/model/user.types';
import { useWheelActions } from '@/entities/wheel/model/wheel.store';

import { showPopup } from '@/shared/helpers/show-popup';
import { logErrorMini } from '@/shared/services/service-logger/lib/log-error-mini';

import { useAdvertisement, type UseAdvertisementProps } from './use-advertisements';

export const usePrioritizedAdvertisement = (
  source: string,
  props: UseAdvertisementProps = {}
) => {
  const { t } = useTranslation();
  const user = useUser();
  const attemptedNetworksRef = useRef<AVD_NETWORK[]>([]);
  const currentNetworkRef = useRef<AVD_NETWORK | null>(null);
  const [isShowing, setIsShowing] = useState(false);
  const { incrementAdsCounter } = useWheelActions();
  const { viewAdForTicket = false } = props;

  const getNextNetwork = useCallback(
    (excludeNetworks: AVD_NETWORK[]): AVD_NETWORK | null => {
      if (user?.avdNetworks && user.avdNetworks.length > 0) {
        const availableNetworks = user.avdNetworks.filter(
          (network) => !excludeNetworks.includes(network)
        );
        return availableNetworks.length > 0 ? availableNetworks[0] : null;
      }

      // Adding fallback: if all networks are exhausted, we give PD because it is available to everyone
      if (!excludeNetworks.includes(AVD_NETWORK.PD)) {
        return AVD_NETWORK.PD;
      }

      return null;
    },
    [user?.avdNetworks]
  );

  const resetAdState = () => {
    setIsShowing(false);
    attemptedNetworksRef.current = [];
    currentNetworkRef.current = null;
  };

  const baseShow = useAdvertisement(source, {
    ...props,
    withPriority: true,
    viewAdForTicket,
    onReward: () => {
      if (source === 'wheel' && viewAdForTicket) {
        void incrementAdsCounter();
      }
      resetAdState();
      props.onReward?.();
    },
    onError: (error) => {
      const currentNetwork = currentNetworkRef.current;
      if (!currentNetwork) {
        setIsShowing(false);
        props.onError?.(error);
        return;
      }

      if (!attemptedNetworksRef.current.includes(currentNetwork)) {
        attemptedNetworksRef.current.push(currentNetwork);
      }

      const nextNetwork = getNextNetwork(attemptedNetworksRef.current);
      if (nextNetwork) {
        currentNetworkRef.current = nextNetwork;
        const scheduleNextAttempt = (network: AVD_NETWORK) => {
          setTimeout(() => baseShow(network), 0);
        };
        scheduleNextAttempt(nextNetwork);
      } else {
        resetAdState();
        void logErrorMini('All ad networks failed', 'use-prioritized-advertisement.ts', {
          attempted: attemptedNetworksRef.current,
        });
        showPopup({ message: t('No ads available at the moment') });
        props.onError?.(error);
      }
    },
    onSkip: () => {
      resetAdState();
      props.onSkip?.();
    },
  });

  const show = useCallback(() => {
    if (isShowing) return;
    attemptedNetworksRef.current = [];
    setIsShowing(true);

    if (user?.avdNetworks && user.avdNetworks.length > 0) {
      const firstNetwork = user.avdNetworks[0];
      currentNetworkRef.current = firstNetwork;
      baseShow(firstNetwork);
    }
    // No networks available
    else {
      setIsShowing(false);
      showPopup({ message: t('No ad networks available') });
      props.onError?.(new Error('No ad networks available'));
    }
  }, [baseShow, user, isShowing, t, props]);

  return {
    show,
    isShowing,
  };
};
